import { supabase } from '@/integrations/supabase/client';

export interface BankTransactionRule {
  id?: string;
  company_id: string;
  account_id?: string;
  rule_name: string;
  pattern: string;
  category_type: 'revenue' | 'expenses' | 'balance_sheet';
  subcategory: string;
  detail_source?: string;
  priority?: number;
  is_active?: boolean;
  rule_type?: 'description_pattern' | 'merchant_name' | 'amount_range' | 'counterparty';
  match_pattern?: Record<string, any>;
  location_id?: string;
  training_transaction_ids?: string[];
  confidence_score?: number;
  match_count?: number;
  pattern_variations?: string[];
  exception_patterns?: string[];
  last_validated_at?: string;
  false_positive_count?: number;
  true_positive_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface LabeledTransaction {
  id: string;
  transaction_date: string;
  amount: number;
  description: string;
  merchant_name?: string;
  category_type: 'revenue' | 'expenses' | 'balance_sheet';
  subcategory: string;
  detail_source?: string;
  location_id?: string;
}

/**
 * Extracts merchant name from description using common patterns
 */
function extractMerchantNameFromDescription(description: string): string | null {
  const desc = description.toLowerCase().trim();
  
  // Common patterns for merchant names in bank descriptions
  // Pattern 1: Merchant name at start before common separators
  const separators = ['|', ' - ', ' #', ' *', ' /', '\\', '  '];
  for (const sep of separators) {
    const parts = desc.split(sep);
    if (parts[0] && parts[0].length > 2) {
      const merchant = parts[0].trim();
      // Filter out common non-merchant prefixes
      if (!merchant.match(/^(purchase|payment|debit|credit|transfer|ach|wire|check)/)) {
        return merchant;
      }
    }
  }
  
  // Pattern 2: Extract capitalized words (common merchant name pattern)
  const capitalizedWords = desc.match(/\b[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\b/);
  if (capitalizedWords && capitalizedWords[0].length > 3) {
    return capitalizedWords[0].toLowerCase();
  }
  
  // Pattern 3: Extract first 2-3 significant words
  const words = desc.split(/\s+/).filter(w => w.length > 2);
  if (words.length >= 2) {
    const candidate = words.slice(0, 2).join(' ');
    if (candidate.length > 4 && !candidate.match(/^(purchase|payment|debit|credit)/)) {
      return candidate;
    }
  }
  
  return null;
}

/**
 * Extracts n-grams (1-3 word phrases) from text
 */
function extractNGrams(text: string, maxN: number = 3): string[] {
  const stopWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'should', 'could', 'may', 'might', 'must', 'can']);
  
  const words = text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 1 && !stopWords.has(word));
  
  const ngrams: string[] = [];
  
  // Extract 1-grams (single words)
  words.forEach(word => {
    if (word.length > 3 && !ngrams.includes(word)) {
      ngrams.push(word);
    }
  });
  
  // Extract 2-grams (two word phrases)
  if (maxN >= 2) {
    for (let i = 0; i < words.length - 1; i++) {
      const bigram = `${words[i]} ${words[i + 1]}`;
      if (bigram.length > 4 && !ngrams.includes(bigram)) {
        ngrams.push(bigram);
      }
    }
  }
  
  // Extract 3-grams (three word phrases)
  if (maxN >= 3) {
    for (let i = 0; i < words.length - 2; i++) {
      const trigram = `${words[i]} ${words[i + 1]} ${words[i + 2]}`;
      if (trigram.length > 6 && !ngrams.includes(trigram)) {
        ngrams.push(trigram);
      }
    }
  }
  
  return ngrams;
}

/**
 * Calculates pattern uniqueness score (higher = more unique/distinctive)
 */
function calculatePatternUniqueness(pattern: string, allPatterns: string[]): number {
  // Shorter patterns are generally less unique
  if (pattern.length < 4) return 0.3;
  if (pattern.length < 6) return 0.5;
  if (pattern.length < 10) return 0.7;
  
  // Check how many other patterns contain this pattern
  const containingCount = allPatterns.filter(p => p.includes(pattern) || pattern.includes(p)).length;
  const uniqueness = 1 - (containingCount / Math.max(allPatterns.length, 1)) * 0.5;
  
  return Math.max(0.3, Math.min(1.0, uniqueness));
}

/**
 * Extracts patterns from a labeled transaction to create a rule
 */
export function extractPatternsFromTransaction(
  transaction: LabeledTransaction
): {
  descriptionPatterns: string[];
  merchantName?: string;
  amountRange?: { min: number; max: number };
  ngrams: string[];
  locationPattern?: string;
} {
  const description = transaction.description.toLowerCase();
  
  // Extract n-grams (1-3 word phrases)
  const ngrams = extractNGrams(description, 3);
  
  // Extract merchant name - prefer explicit merchant_name, fallback to description extraction
  let merchantName = transaction.merchant_name?.toLowerCase().trim();
  if (!merchantName) {
    merchantName = extractMerchantNameFromDescription(transaction.description) || undefined;
  }
  
  // Create amount range (±15% of transaction amount for more flexibility)
  const amount = Math.abs(transaction.amount);
  const amountRange = {
    min: amount * 0.85,
    max: amount * 1.15,
  };
  
  // Extract meaningful patterns prioritizing longer, more unique patterns
  const allPatterns = [...ngrams];
  const patterns = allPatterns
    .map(p => ({
      pattern: p,
      uniqueness: calculatePatternUniqueness(p, allPatterns),
    }))
    .sort((a, b) => b.uniqueness - a.uniqueness)
    .slice(0, 10) // Top 10 most unique patterns
    .map(p => p.pattern);
  
  // Add location pattern if available
  const locationPattern = transaction.location_id ? `location:${transaction.location_id}` : undefined;
  
  return {
    descriptionPatterns: patterns,
    merchantName,
    amountRange,
    ngrams,
    locationPattern,
  };
}

/**
 * Generates a rule from a labeled transaction with enhanced pattern extraction
 */
export async function generateRuleFromTransaction(
  transaction: LabeledTransaction,
  companyId: string,
  accountId?: string
): Promise<BankTransactionRule> {
  const patterns = extractPatternsFromTransaction(transaction);
  
  // Determine rule type based on available data
  // Use composite rule if we have multiple strong signals
  const hasMerchant = !!patterns.merchantName;
  const hasAmount = !!patterns.amountRange;
  const hasLocation = !!patterns.locationPattern;
  const hasDescription = patterns.descriptionPatterns.length > 0;
  
  let ruleType: 'description_pattern' | 'merchant_name' | 'amount_range' | 'counterparty' = 'description_pattern';
  let matchPattern: Record<string, any> = {};
  let pattern: string = '';
  let patternVariations: string[] = [];
  
  // Create composite rule if we have merchant + amount + location or merchant + description
  // Use merchant_name as the rule_type (strongest signal), but store composite logic in match_pattern
  if (hasMerchant && (hasAmount || hasLocation || hasDescription)) {
    ruleType = 'merchant_name'; // Use merchant_name as the base type for composite rules
    pattern = patterns.merchantName || patterns.descriptionPatterns[0] || '';
    matchPattern = {
      type: 'composite',
      merchant_name: patterns.merchantName,
      description_patterns: patterns.descriptionPatterns.slice(0, 5),
      amount_range: patterns.amountRange,
      location_id: transaction.location_id,
      fuzzy: true,
    };
    patternVariations = [
      ...(patterns.merchantName ? [patterns.merchantName] : []),
      ...patterns.descriptionPatterns.slice(0, 5),
    ];
  } else if (hasMerchant) {
    ruleType = 'merchant_name';
    pattern = patterns.merchantName!;
    matchPattern = {
      type: 'merchant_name',
      value: patterns.merchantName,
      fuzzy: true,
    };
    patternVariations = [patterns.merchantName!];
  } else if (hasDescription) {
    ruleType = 'description_pattern';
    // Use the most distinctive pattern (longest or most unique)
    pattern = patterns.descriptionPatterns.sort((a, b) => b.length - a.length)[0];
    matchPattern = {
      type: 'description_pattern',
      patterns: patterns.descriptionPatterns.slice(0, 5), // Top 5 patterns
      fuzzy: true,
    };
    patternVariations = patterns.descriptionPatterns.slice(0, 5);
  }

  // Fallback for onboarding: when extraction yields no pattern (short/generic description),
  // use the raw description so a rule is always created and future transactions can match.
  if (!pattern || patternVariations.length === 0) {
    const fallback = (transaction.description || '').trim().slice(0, 100) || 'transaction';
    pattern = fallback;
    ruleType = 'description_pattern';
    matchPattern = {
      type: 'description_pattern',
      patterns: [fallback],
      fuzzy: true,
    };
    patternVariations = [fallback];
  }

  // Add amount range if available (even for non-composite rules)
  if (patterns.amountRange) {
    matchPattern.amountRange = patterns.amountRange;
  }
  
  // Calculate initial confidence based on pattern uniqueness and data quality
  let initialConfidence = 0.5;
  if (hasMerchant) initialConfidence += 0.2;
  if (hasAmount) initialConfidence += 0.1;
  if (hasLocation) initialConfidence += 0.1;
  if (patterns.descriptionPatterns.length > 3) initialConfidence += 0.1;
  initialConfidence = Math.min(0.85, initialConfidence);
  
  // Generate rule name
  const ruleName = patterns.merchantName 
    ? `${patterns.merchantName} - ${transaction.subcategory}`
    : `${transaction.subcategory} - ${pattern.substring(0, 30)}`;
  
  // Check if similar rule already exists
  const existingRule = await findSimilarRule(
    companyId,
    accountId,
    ruleType,
    pattern,
    transaction.category_type,
    transaction.subcategory
  );
  
  if (existingRule) {
    // Update existing rule: add transaction ID to training set, increment match count
    const updatedTrainingIds = [
      ...(existingRule.training_transaction_ids || []),
      transaction.id,
    ];
    
    // Merge pattern variations
    const existingVariations = existingRule.pattern_variations || [];
    const mergedVariations = Array.from(new Set([...existingVariations, ...patternVariations]));
    
    const updateData: any = {
      match_count: (existingRule.match_count || 0) + 1,
      training_transaction_ids: updatedTrainingIds,
      confidence_score: calculateConfidenceScore(updatedTrainingIds.length),
      pattern_variations: mergedVariations.slice(0, 10), // Keep top 10 variations
      updated_at: new Date().toISOString(),
    };
    
    // Update match_pattern if we have new patterns
    if (patternVariations.length > 0) {
      const existingMatchPattern = existingRule.match_pattern || {};
      if (existingMatchPattern.type === 'description_pattern' || existingMatchPattern.type === 'composite') {
        const existingPatterns = existingMatchPattern.patterns || existingMatchPattern.description_patterns || [];
        const allPatterns = Array.from(new Set([...existingPatterns, ...patternVariations]));
        updateData.match_pattern = {
          ...existingMatchPattern,
          patterns: allPatterns.slice(0, 10),
          description_patterns: allPatterns.slice(0, 10),
        };
      }
    }
    
    const { data, error } = await supabase
      .from('bank_transaction_rules')
      .update(updateData)
      .eq('id', existingRule.id)
      .select()
      .single();
    
    if (error) throw error;
    return data as BankTransactionRule;
  }
  
  // Create new rule
  const rule: Omit<BankTransactionRule, 'id' | 'created_at' | 'updated_at'> = {
    company_id: companyId,
    account_id: accountId,
    rule_name: ruleName,
    pattern,
    category_type: transaction.category_type,
    subcategory: transaction.subcategory,
    detail_source: transaction.detail_source,
    priority: 0,
    is_active: true,
    rule_type: ruleType,
    match_pattern: matchPattern,
    location_id: transaction.location_id,
    training_transaction_ids: [transaction.id],
    confidence_score: initialConfidence,
    match_count: 1,
    pattern_variations: patternVariations.slice(0, 10),
    exception_patterns: [],
    false_positive_count: 0,
    true_positive_count: 0,
  };
  
  const { data, error } = await supabase
    .from('bank_transaction_rules')
    .insert(rule)
    .select()
    .single();
  
  if (error) throw error;
  return data as BankTransactionRule;
}

/**
 * Finds similar existing rule
 */
async function findSimilarRule(
  companyId: string,
  accountId: string | undefined,
  ruleType: string,
  pattern: string,
  categoryType: string,
  subcategory: string
): Promise<BankTransactionRule | null> {
  // Build query with conditional account filter to reduce type depth
  // Use type assertion early to break deep type chain
  const baseQuery = (supabase
    .from('bank_transaction_rules') as any)
    .select('*')
    .eq('company_id', companyId)
    .eq('rule_type', ruleType)
    .eq('category_type', categoryType)
    .eq('subcategory', subcategory)
    .eq('is_active', true);
  
  // Use conditional filter in a single chain to reduce type complexity
  const finalQuery = accountId 
    ? baseQuery.eq('account_id', accountId)
    : baseQuery;
  
  const { data, error } = await finalQuery;
  
  if (error) throw error;
  
  if (!data || data.length === 0) return null;
  
  // Find rule with similar pattern (fuzzy match)
  const patternLower = pattern.toLowerCase();
  for (const rule of data as any[]) {
    const rulePattern = (rule.pattern || '').toLowerCase();
    if (rulePattern.includes(patternLower) || patternLower.includes(rulePattern)) {
      return rule as BankTransactionRule;
    }
  }
  
  return null;
}

/**
 * Calculates confidence score based on number of training examples
 */
function calculateConfidenceScore(trainingCount: number): number {
  // Confidence increases with more examples, capped at 0.95
  return Math.min(0.5 + (trainingCount * 0.1), 0.95);
}

/**
 * Generates multiple rules from a batch of labeled transactions
 */
export async function generateRulesFromTransactions(
  transactions: LabeledTransaction[],
  companyId: string,
  accountId?: string
): Promise<BankTransactionRule[]> {
  const rules: BankTransactionRule[] = [];
  
  // Group transactions by category/subcategory for better rule generation
  const grouped = transactions.reduce((acc, tx) => {
    const key = `${tx.category_type}:${tx.subcategory}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(tx);
    return acc;
  }, {} as Record<string, LabeledTransaction[]>);
  
  // Generate rules for each group
  for (const group of Object.values(grouped)) {
    // Use the first transaction as the base, but consider all for pattern extraction
    const baseTransaction = group[0];
    const rule = await generateRuleFromTransaction(baseTransaction, companyId, accountId);
    rules.push(rule);
    
    // If there are multiple transactions in the group, update the rule with all IDs
    if (group.length > 1) {
      const allIds = group.map(tx => tx.id);
      const { error } = await supabase
        .from('bank_transaction_rules')
        .update({
          training_transaction_ids: allIds as any,
          match_count: group.length,
          confidence_score: calculateConfidenceScore(group.length),
        } as any)
        .eq('id', rule.id);
      
      if (error) {
        console.error('Error updating rule with multiple transactions:', error);
      }
    }
  }
  
  return rules;
}

/**
 * Updates a rule's confidence and match count
 */
export async function updateRuleConfidence(
  ruleId: string,
  matchCount: number
): Promise<void> {
  const confidenceScore = calculateConfidenceScore(matchCount);
  
  const { error } = await supabase
    .from('bank_transaction_rules')
    .update({
      match_count: matchCount,
      confidence_score: confidenceScore,
      updated_at: new Date().toISOString(),
    })
    .eq('id', ruleId);
  
  if (error) throw error;
}

